import { actorRefFromLegacy } from "@brains/contracts";
import {
  type BaseEntity,
  type ContentVisibility,
  type Conversation,
  type EntityPluginContext,
  type Message,
  type SearchResult,
} from "@brains/plugins";
import type { Logger } from "@brains/utils/logger";
import { z } from "@brains/utils/zod";
import { contentVisibilitySchema } from "@brains/plugins";
import { computeContentHash } from "@brains/utils/hash";
import {
  actionItemSchema,
  decisionSchema,
  type ActionItemEntity,
  type DecisionEntity,
} from "../schemas/conversation-memory";
import { summarySchema, type SummaryEntity } from "../schemas/summary";
import type { SummaryConfig } from "../schemas/summary-config";
import { SummaryExtractor } from "./summary-extractor";
import { ConversationMemoryRetriever } from "./conversation-memory-retriever";
import { SummaryProjector } from "./summary-projector";
import { buildConversationMemoryAgentContext } from "./agent-context-provider";
import { buildFallbackExcerpt } from "./excerpt";
import { getConversationSpaceId } from "./summary-space-eligibility";

const messageRoleSchema = z.enum(["user", "assistant"]);

const conversationMessageActorSchema = z.object({
  actorId: z.string(),
  canonicalId: z.string().optional(),
  interfaceType: z.string(),
  role: messageRoleSchema,
  displayName: z.string().optional(),
  username: z.string().optional(),
  isBot: z.boolean().optional(),
});

const conversationMessageSourceSchema = z.object({
  messageId: z.string().optional(),
  channelId: z.string().optional(),
  channelName: z.string().optional(),
  threadId: z.string().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

const evalMessageSchema = z.object({
  role: messageRoleSchema,
  content: z.string(),
  timestamp: z.string().datetime().optional(),
  actor: conversationMessageActorSchema.optional(),
  source: conversationMessageSourceSchema.optional(),
});

const summarizeMessagesInputSchema = z.object({
  conversationId: z.string().default("eval-conversation"),
  messages: z.array(evalMessageSchema),
});

const projectConversationInputSchema = z.object({
  conversationId: z.string(),
});

const projectMessagesInputSchema = z.object({
  conversationId: z.string().default("eval-conversation"),
  interfaceType: z.string().default("eval"),
  channelId: z.string().default("eval-channel"),
  channelName: z.string().optional(),
  projectionDecision: z.enum(["update", "append"]).default("update"),
  existingSummary: z.string().optional(),
  existingMessageCount: z.number().int().min(0).default(0),
  messages: z.array(evalMessageSchema),
});

const seededActorReferenceSchema = z.object({
  actorId: z.string(),
  canonicalId: z.string().optional(),
  displayName: z.string().optional(),
});

const seededParticipantSchema = seededActorReferenceSchema.extend({
  roles: z.array(z.enum(["user", "assistant", "system"])).default(["user"]),
  sourceActorIds: z.array(z.string()).optional(),
});

const seededAssigneeSchema = z.object({
  actorId: z.string().optional(),
  canonicalId: z.string().optional(),
  displayName: z.string(),
});

const seededMemorySchema = z.object({
  id: z.string(),
  entityType: z.enum(["summary", "decision", "action-item"]),
  content: z.string(),
  excerpt: z.string().optional(),
  score: z.number().optional(),
  conversationId: z.string(),
  interfaceType: z.string(),
  channelId: z.string(),
  channelName: z.string().optional(),
  updated: z.string().datetime().optional(),
  visibility: contentVisibilitySchema,
  status: z.string().optional(),
  participants: z.array(seededParticipantSchema).optional(),
  decidedBy: z.array(seededActorReferenceSchema).optional(),
  mentionedBy: z.array(seededActorReferenceSchema).optional(),
  assignedTo: z.array(seededAssigneeSchema).optional(),
  requestedBy: z.array(seededActorReferenceSchema).optional(),
});

const retrieveMemoryInputSchema = z.object({
  query: z.string().optional(),
  conversationId: z.string().optional(),
  interfaceType: z.string().optional(),
  channelId: z.string().optional(),
  limit: z.number().int().min(1).optional(),
  includeOtherSpaces: z.boolean().optional(),
  actorId: z.string().optional(),
  canonicalId: z.string().optional(),
  visibilityScope: contentVisibilitySchema.optional(),
  memory: z.array(seededMemorySchema).optional(),
});

const agentContextInputSchema = z.object({
  conversationId: z.string().default("eval-conversation"),
  message: z.string(),
  interfaceType: z.string().default("eval"),
  channelId: z.string().optional(),
  channelName: z.string().optional(),
  userPermissionLevel: z
    .enum(["admin", "trusted", "public"])
    .default("trusted"),
  memory: z.array(seededMemorySchema).optional(),
});

const decideProjectionInputSchema = z.object({
  conversationId: z.string().default("eval-conversation"),
  existingSummary: z.string().optional(),
  existingMessageCount: z.number().int().min(0).default(0),
  messages: z.array(evalMessageSchema),
});

export function registerSummaryEvalHandlers(params: {
  context: EntityPluginContext;
  logger: Logger;
  config: SummaryConfig;
}): void {
  const { context, logger, config } = params;

  context.eval.registerHandler("summarizeMessages", async (input: unknown) => {
    const parsed = summarizeMessagesInputSchema.parse(input);
    const messages = toEvalMessages(parsed.messages, parsed.conversationId);

    const extractor = new SummaryExtractor(context, logger, config);
    const memory = await extractor.extract(messages);
    return memory.entries.map((entry) => {
      const decisions = memory.decisions
        .filter((item) => item.timeRange.start >= entry.timeRange.start)
        .filter((item) => item.timeRange.end <= entry.timeRange.end)
        .map((item) => item.text);
      const actionItems = memory.actionItems
        .filter((item) => item.timeRange.start >= entry.timeRange.start)
        .filter((item) => item.timeRange.end <= entry.timeRange.end)
        .map((item) => item.text);
      return {
        ...entry,
        decisions,
        actionItems,
        keyPointsText: entry.keyPoints.join("\n"),
        decisionsText: decisions.join("\n"),
        actionItemsText: actionItems.join("\n"),
      };
    });
  });

  context.eval.registerHandler("decideProjection", async (input: unknown) => {
    const parsed = decideProjectionInputSchema.parse(input);
    const messages = toEvalMessages(parsed.messages, parsed.conversationId);

    const existing = parsed.existingSummary
      ? createEvalSummaryEntity({
          conversationId: parsed.conversationId,
          content: parsed.existingSummary,
          messageCount: parsed.existingMessageCount,
          projectionVersion: config.projectionVersion,
          visibility: config.memoryVisibility,
        })
      : null;

    const projector = new SummaryProjector(context, logger, config);
    return projector.decideProjection(messages, existing);
  });

  context.eval.registerHandler("retrieveMemory", async (input: unknown) => {
    const parsed = retrieveMemoryInputSchema.parse(input);
    const retrievalContext = parsed.memory
      ? createSeededRetrievalContext(context, parsed.memory)
      : context;
    const retriever = new ConversationMemoryRetriever(retrievalContext);
    const { actorId, canonicalId, memory: _memory, ...retrievalInput } = parsed;
    const legacyIdentity = actorId ?? canonicalId;
    return retriever.retrieve({
      ...retrievalInput,
      ...(legacyIdentity
        ? {
            identity: actorRefFromLegacy({
              actorId: legacyIdentity,
              interfaceType: sourceFromLegacyActorId(legacyIdentity),
              role: "user",
              ...(canonicalId ? { canonicalId } : {}),
            }),
          }
        : {}),
    });
  });

  context.eval.registerHandler("buildAgentContext", async (input: unknown) => {
    const parsed = agentContextInputSchema.parse(input);
    const retrievalContext = parsed.memory
      ? createSeededRetrievalContext(context, parsed.memory)
      : context;
    return buildConversationMemoryAgentContext(retrievalContext, parsed);
  });

  context.eval.registerHandler("projectMessages", async (input: unknown) => {
    const parsed = projectMessagesInputSchema.parse(input);
    const messages = toEvalMessages(parsed.messages, parsed.conversationId);
    const conversation = createEvalConversation({
      conversationId: parsed.conversationId,
      interfaceType: parsed.interfaceType,
      channelId: parsed.channelId,
      channelName: parsed.channelName,
      messages,
    });
    const existing = parsed.existingSummary
      ? createEvalSummaryEntity({
          conversationId: parsed.conversationId,
          content: parsed.existingSummary,
          messageCount: parsed.existingMessageCount,
          projectionVersion: config.projectionVersion,
          visibility: config.memoryVisibility,
        })
      : null;
    const upserted: BaseEntity[] = [];
    const deleted: Array<{ entityType: string; id: string }> = [];
    const projectionContext = createEvalProjectionContext({
      context,
      conversation,
      messages,
      existing,
      upserted,
      deleted,
      projectionDecision: parsed.projectionDecision,
    });
    const projector = new SummaryProjector(projectionContext, logger, config);
    const result = await projector.projectConversation(parsed.conversationId);

    return {
      result,
      summaries: upserted.filter((entity) => entity.entityType === "summary"),
      decisions: upserted.filter((entity) => entity.entityType === "decision"),
      actionItems: upserted.filter(
        (entity) => entity.entityType === "action-item",
      ),
      deleted,
    };
  });

  context.eval.registerHandler(
    "projectConversation",
    async (input: unknown) => {
      const parsed = projectConversationInputSchema.parse(input);
      const projector = new SummaryProjector(context, logger, config);
      return projector.projectConversation(parsed.conversationId);
    },
  );
}

function createEvalSummaryEntity(params: {
  conversationId: string;
  content: string;
  messageCount: number;
  projectionVersion: number;
  visibility: ContentVisibility;
}): SummaryEntity {
  const now = "2026-01-01T00:00:00.000Z";
  return {
    id: params.conversationId,
    entityType: "summary",
    content: params.content,
    contentHash: "eval-existing-summary",
    visibility: params.visibility,
    created: now,
    updated: now,
    metadata: {
      conversationId: params.conversationId,
      channelId: "eval-channel",
      interfaceType: "eval",
      messageCount: params.messageCount,
      entryCount: 1,
      sourceHash: "eval-source-hash",
      projectionVersion: params.projectionVersion,
    },
  };
}

function toEvalMessages(
  messages: z.output<typeof evalMessageSchema>[],
  conversationId: string,
): Message[] {
  return messages.map((message, index): Message => {
    const timestamp =
      message.timestamp ??
      new Date(Date.UTC(2026, 0, 1, 0, index)).toISOString();
    return {
      id: `eval-message-${index + 1}`,
      conversationId,
      role: message.role,
      content: message.content,
      timestamp,
      metadata: {
        ...(message.actor ? { actor: message.actor } : {}),
        ...(message.source ? { source: message.source } : {}),
      },
    };
  });
}

function createEvalConversation(params: {
  conversationId: string;
  interfaceType: string;
  channelId: string;
  channelName?: string | undefined;
  messages: Message[];
}): Conversation {
  const firstMessage = params.messages[0];
  const lastMessage = params.messages[params.messages.length - 1];
  const startedAt = firstMessage?.timestamp ?? "2026-01-01T00:00:00.000Z";
  const lastActiveAt = lastMessage?.timestamp ?? startedAt;
  return {
    id: params.conversationId,
    sessionId: params.conversationId,
    interfaceType: params.interfaceType,
    channelId: params.channelId,
    ...(params.channelName ? { channelName: params.channelName } : {}),
    startedAt,
    lastActiveAt,
    createdAt: startedAt,
    updatedAt: lastActiveAt,
    metadata: {},
  };
}

function createEvalProjectionContext(params: {
  context: EntityPluginContext;
  conversation: Conversation;
  messages: Message[];
  existing: SummaryEntity | null;
  upserted: BaseEntity[];
  deleted: Array<{ entityType: string; id: string }>;
  projectionDecision: "update" | "append";
}): EntityPluginContext {
  const spaceId = getConversationSpaceId(params.conversation);
  const context: EntityPluginContext = {
    ...params.context,
    spaces: [spaceId],
    conversations: {
      ...params.context.conversations,
      get: async () => params.conversation,
      getMessages: async () => params.messages,
    },
    ai: {
      ...params.context.ai,
      // generateObject promises the caller's chosen T, which a fixed stub
      // cannot produce. The assertion is scoped to this one member so every
      // other override in this context is still checked against the real type.
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- eval stub: generateObject is generic over a caller-chosen output, so a fixed forced value cannot satisfy an arbitrary T
      generateObject: (async () => ({
        object: {
          decision: params.projectionDecision,
          rationale: "Forced by eval input",
        },
      })) as EntityPluginContext["ai"]["generateObject"],
    },
    entityService: {
      ...params.context.entityService,
      getEntity: async ({ entityType }: { entityType: string }) =>
        entityType === "summary" ? params.existing : null,
      listEntities: async () => [],
      deleteEntity: async (request: { entityType: string; id: string }) => {
        params.deleted.push(request);
        return true;
      },
      upsertEntity: async <T extends BaseEntity>({ entity }: { entity: T }) => {
        params.upserted.push(entity);
        return {
          entityId: entity.id,
          jobId: "eval-upsert",
          created: true,
          skipped: false,
        };
      },
    },
  };
  return context;
}

type SeededMemory = z.output<typeof seededMemorySchema>;

type EvalMemoryEntity = SummaryEntity | DecisionEntity | ActionItemEntity;

function createSeededRetrievalContext(
  context: EntityPluginContext,
  memory: SeededMemory[],
): EntityPluginContext {
  const entities = memory.map(toMemoryEntity);
  const searchResults: SearchResult<EvalMemoryEntity>[] = entities.map(
    (entity, index) => ({
      entity,
      score: memory[index]?.score ?? 1,
      excerpt: memory[index]?.excerpt ?? buildFallbackExcerpt(entity),
    }),
  );

  const seeded: EntityPluginContext = {
    ...context,
    entityService: {
      ...context.entityService,
      search: async () => searchResults,
      listEntities: async ({ entityType }: { entityType: string }) =>
        entities.filter((entity) => entity.entityType === entityType),
    },
  };
  return seeded;
}

function toMemoryEntity(memory: SeededMemory): EvalMemoryEntity {
  if (memory.entityType === "summary") return toSummaryEntity(memory);
  if (memory.entityType === "decision") return toDecisionEntity(memory);
  return toActionItemEntity(memory);
}

function baseMemoryFields(
  memory: SeededMemory,
): Pick<
  BaseEntity,
  "id" | "content" | "contentHash" | "created" | "updated"
> & { visibility: ContentVisibility } {
  const updated = memory.updated ?? "2026-01-01T00:00:00.000Z";
  return {
    id: memory.id,
    content: memory.content,
    contentHash: computeContentHash(memory.content),
    visibility: memory.visibility,
    created: updated,
    updated,
  };
}

function toSummaryEntity(memory: SeededMemory): SummaryEntity {
  return summarySchema.parse({
    ...baseMemoryFields(memory),
    entityType: "summary",
    metadata: {
      conversationId: memory.conversationId,
      channelId: memory.channelId,
      ...(memory.channelName ? { channelName: memory.channelName } : {}),
      interfaceType: memory.interfaceType,
      messageCount: 3,
      entryCount: 1,
      sourceHash: `source-${memory.id}`,
      projectionVersion: 1,
      ...(memory.participants ? { participants: memory.participants } : {}),
    },
  });
}

function toDecisionEntity(memory: SeededMemory): DecisionEntity {
  return decisionSchema.parse({
    ...baseMemoryFields(memory),
    entityType: "decision",
    metadata: {
      conversationId: memory.conversationId,
      channelId: memory.channelId,
      ...(memory.channelName ? { channelName: memory.channelName } : {}),
      interfaceType: memory.interfaceType,
      spaceId: getConversationSpaceId(memory),
      timeRange: {
        start: "2026-01-01T00:00:00.000Z",
        end: "2026-01-01T00:01:00.000Z",
      },
      sourceSummaryId: memory.conversationId,
      sourceMessageCount: 2,
      projectionVersion: 1,
      status: memory.status === "superseded" ? "superseded" : "active",
      ...(memory.decidedBy ? { decidedBy: memory.decidedBy } : {}),
      ...(memory.mentionedBy ? { mentionedBy: memory.mentionedBy } : {}),
    },
  });
}

function toActionItemEntity(memory: SeededMemory): ActionItemEntity {
  const status =
    memory.status === "done" || memory.status === "dropped"
      ? memory.status
      : "open";
  return actionItemSchema.parse({
    ...baseMemoryFields(memory),
    entityType: "action-item",
    metadata: {
      conversationId: memory.conversationId,
      channelId: memory.channelId,
      ...(memory.channelName ? { channelName: memory.channelName } : {}),
      interfaceType: memory.interfaceType,
      spaceId: getConversationSpaceId(memory),
      timeRange: {
        start: "2026-01-01T00:00:00.000Z",
        end: "2026-01-01T00:01:00.000Z",
      },
      sourceSummaryId: memory.conversationId,
      sourceMessageCount: 2,
      projectionVersion: 1,
      status,
      ...(memory.assignedTo ? { assignedTo: memory.assignedTo } : {}),
      ...(memory.requestedBy ? { requestedBy: memory.requestedBy } : {}),
    },
  });
}

function sourceFromLegacyActorId(actorId: string): string {
  const separator = actorId.indexOf(":");
  return separator > 0 ? actorId.slice(0, separator) : "legacy";
}
